import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Button,
  PasswordInput,
  TextInput,
  Text,
  Flex,
  createTheme,
  Input,
  MantineProvider,
  Alert,
} from "@mantine/core";
import {
  IconCircleX,
  IconKey,
  IconLock,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { toast } from "react-toastify";
import classes from "./Login.module.css";
import { useModal } from "./contexts/useModal";
import { WorkerUserGenerate } from "./components/Modal";
import axiosInstance from "./axiosInstance";
import { setActionLog } from "./utils/util";

const Login: React.FC = () => {
  const [userId, setuserId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const userIdInputRef = useRef<HTMLInputElement>(null);
  const { showModal } = useModal();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [logoText, setLogoText] = useState("MES");
  const [logoColor, setLogoColor] = useState<string | undefined>(undefined);

  interface RefreshResponse {
    data: {
      accessToken: string;
      refreshToken: string;
    };
  }

  useEffect(() => {
    userIdInputRef.current?.focus();

    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
      // 로컬 개발 환경: 빨간색 MES
      setLogoText("MES");
      setLogoColor("red");
    } else if (host === "192.168.0.237") {
      // 237 서버: MES(237)
      setLogoText("MES(237)");
    }
    // 그 외(프로덕션): 기본값 "MES" / 기본 색상 유지
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      /*
      const res = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, password }),
      });
      if (!res.ok) throw new Error("로그인 실패");
      const responseData = await res.json();
      */
      const { data }: { data: RefreshResponse } = await axios.post(
        "/api/v1/auth/login",
        JSON.stringify({ userId, password }),
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      localStorage.setItem("accessToken", data.data.accessToken);
      localStorage.setItem("refreshToken", data.data.refreshToken);

      const res = await axiosInstance.get("/api/v1/auth/user/roles");
      const d = (res.data.data as any) || [];
      if (d.length > 0) {
        await setActionLog({ eventType: "LOGIN", title: "로그인" });
        window.location.href = "/Terminals";
      } else {
        localStorage.clear();
        setErrorMessage("접근권한이 없습니다. \n 관리자에게 문의 바랍니다.");
      }
    } catch (refreshError: any) {
      console.log(refreshError);
      setErrorMessage("아이디 혹은 비밀번호가 맞지 않습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleUserGenerate = async () => {
    await showModal({ component: WorkerUserGenerate, props: {} });
  };

  const theme = createTheme({
    components: {
      Input: Input.extend({
        classNames: {
          input: classes.input,
        },
      }),
    },
  });

  return (
    <MantineProvider theme={theme}>
      <div
        style={{
          width: "100%",
          height: "100vh",
          overflow: "hidden",
          background: "rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            maxWidth: 460,
            margin: "80px auto",
            padding: "32px 56px",
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <Text fz={72} fw={300} ta="center" c={logoColor}>
            {logoText}
          </Text>
          <Text mt={36} fz={18} fw={700}>
            로그인
          </Text>
          <form onSubmit={handleSubmit}>
            <TextInput
              ref={userIdInputRef}
              mt="md"
              size="lg"
              placeholder="아이디"
              value={userId}
              onChange={(e) => setuserId(e.target.value)}
              onKeyDown={(e) => {
                e.key === "Enter" && handleSubmit(e);
              }}
              leftSection={<IconUser size={24} stroke={1.5} />}
            />
            <PasswordInput
              mt="md"
              size="lg"
              placeholder="비밀번호"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                e.key === "Enter" && handleSubmit(e);
              }}
              leftSection={<IconKey size={24} stroke={1.5} />}
            />
            <Button
              fullWidth
              type="submit"
              mt="lg"
              size="lg"
              color="#222b45"
              loading={loading}
              loaderProps={{ type: "dots" }}
            >
              로그인
            </Button>
          </form>
          {/* <Flex mt="md" mb="md" justify={"flex-end"}>
            <Button variant="default" onClick={handleUserGenerate}>
              작업자 등록
            </Button>
          </Flex> */}
          {errorMessage && (
            <Alert
              variant="light"
              color="red"
              title="Alert title"
              icon={<IconCircleX />}
              style={{ whiteSpace: "pre-line" }}
            >
              {errorMessage}
            </Alert>
          )}
        </div>
      </div>
    </MantineProvider>
  );
};

export default Login;
